// addAppComposerFocus.js — the app builder opens with the focus in its chat
// composer (persona round 5, sam, keyboard only: after Enter on the welcome's
// "Build my app" the builder opened with the focus on the page body, and the
// way to the chat was a Tab walk from the top through the nav).
//
// The builder's canvas (pages/add-app.js BuildCanvas) calls it on its mount
// and on every update (a transport change is one): the composer's field is
// disabled until the transport is open (shared Chat: `bridgeStatus`), and a
// disabled field cannot take the focus, so the open transport's update is the
// one that lands it on a cold load.
//
// A cold load mounts the builder under the boot screen's `inert` lock
// (index.html lockBodyInput, released on `smbls:boot-ready`, ~3 s on dev): a
// focus there is a silent no-op. An attempt under the lock stops at once and
// the canvas asks again when the lock lifts (its `onWindowSmbls:boot-ready`)
// — the empty builder has no later update that would (W16b live check).
//
// It takes the focus ONCE per mount, and only a focus nobody holds (the page
// body, or a node the route change removed): a link or a control the person
// moved to keeps it. Only where the builder IS the route — the home's Build
// tab (`scope.addAppThreadInUrl: false`) has the home's own composer.
//
// FRANK: self-contained; the canvas element is `this` (el.call).

// → true when an attempt was scheduled.
export function addAppComposerFocus() {
  const el = this
  if (!el || el._addAppComposerFocusDone) return false
  if (el.scope && el.scope.addAppThreadInUrl === false) return false
  const root = el.state && el.state.root
  if (!root || root.bridgeStatus !== 'open') return false
  if (el._addAppComposerFocusTry) return true
  el._addAppComposerFocusTry = true
  const field = () => {
    const main = typeof el.lookup === 'function' ? el.lookup('AppMain') : null
    const chat = main && main.Conversation && main.Conversation.Chat
    const box = chat && chat.ComposerWrap && chat.ComposerWrap.PromptTextarea
    return (box && box.Textarea) || null
  }
  const attempt = (left) => {
    let again = left > 0
    try {
      const node = (field() || {}).node
      if (node && typeof node.closest === 'function' && node.closest('[inert]')) {
        // the boot screen's lock (index.html lockBodyInput): a focus is a
        // no-op until it lifts, and frames end long before it does — the
        // canvas asks again on `smbls:boot-ready`
        again = false
      } else if (node && node.isConnected && typeof node.focus === 'function' && !node.disabled) {
        const doc = node.ownerDocument
        const active = doc && doc.activeElement
        const free = !active || active === doc.body || !active.isConnected
        if (!free && active !== node) {
          // the person is somewhere already: never take it from them
          el._addAppComposerFocusDone = true
          again = false
        } else {
          if (active !== node) node.focus({ preventScroll: true })
          if (doc.activeElement === node) {
            el._addAppComposerFocusDone = true
            again = false
          }
        }
      }
    } catch (_) {
      /* focus is a courtesy — never an error */
    }
    if (again) {
      if (typeof requestAnimationFrame === 'function')
        requestAnimationFrame(() => attempt(left - 1))
      else setTimeout(() => attempt(left - 1), 16)
    } else el._addAppComposerFocusTry = false
  }
  setTimeout(() => attempt(10), 0)
  return true
}
